import { useState, useEffect } from 'react';
import {
  useAdminTeams,
  useDeleteTeam,
  useChangeTeamStatus,
  useExtendTeamTrial,
  useCreateTeam,
  useAdminPackages,
  useConvertTeamToAgency,
} from '@/hooks/useAdmin';
import { AdminTeamListFilters, AdminTeamListItem } from '@/types/admin';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ActionButton } from '@/components/admin/ActionButton';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { CreateTeamRequest, CreateTeamResponse } from '@/services/admin.service';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { Badge, Card, EmptyState, Input, Modal, Pagination, ConfirmDialog, PromptDialog } from '@/components/ui';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { Select } from '@/components/ui/Select';
import { formatApiError } from '@/services/api';
import { useDebounce } from '@/hooks/useDebounce';

/**
 * Admin Teams Page
 * List and manage all teams
 */

const STATUSES = ['ACTIVE', 'GRACE_PERIOD', 'EXPIRED', 'CANCELLED', 'SUSPENDED'] as const;

// Colors for the status-change <Select> control itself (NOT a Badge — this
// tints the actual dropdown trigger, so it stays a raw class map).
const statusColors: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  GRACE_PERIOD: 'bg-yellow-100 text-yellow-700',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
  SUSPENDED: 'bg-orange-100 text-orange-700',
};

export const AdminTeams = () => {
  const { formatDateOnly } = useTimezone();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<AdminTeamListFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  useEffect(() => {
    setFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; team: AdminTeamListItem | null }>({ open: false, team: null });
  const [extendDialog, setExtendDialog] = useState<{ open: boolean; team: AdminTeamListItem | null }>({ open: false, team: null });
  const [convertDialog, setConvertDialog] = useState<{ open: boolean; team: AdminTeamListItem | null }>({ open: false, team: null });

  const { data, isLoading, error } = useAdminTeams({ ...filters, search: debouncedSearch || undefined });
  const deleteTeam = useDeleteTeam();
  const changeStatus = useChangeTeamStatus();
  const extendTrial = useExtendTeamTrial();
  const createTeam = useCreateTeam();
  const convertToAgency = useConvertTeamToAgency();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.team) return;
    try {
      await deleteTeam.mutateAsync(deleteDialog.team.id);
      toast.success(`Team ${deleteDialog.team.name} deleted`);
      setDeleteDialog({ open: false, team: null });
    } catch (err) {
      toast.error('Failed to delete team');
    }
  };

  const handleStatusChange = async (team: AdminTeamListItem, newStatus: typeof STATUSES[number]) => {
    try {
      await changeStatus.mutateAsync({ id: team.id, data: { status: newStatus } });
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleExtendConfirm = async (days: string) => {
    if (!extendDialog.team) return;

    const daysNum = parseInt(days, 10);
    if (isNaN(daysNum) || daysNum < 1 || daysNum > 365) {
      toast.error('Please enter a valid number of days (1-365)');
      return;
    }

    try {
      const result = await extendTrial.mutateAsync({ id: extendDialog.team.id, data: { days: daysNum } });
      toast.success(`Trial extended! New expiry: ${formatDateOnly(result.newExpiresAt)}`);
      setExtendDialog({ open: false, team: null });
    } catch (err) {
      toast.error('Failed to extend trial');
    }
  };

  const handleConvertConfirm = async () => {
    if (!convertDialog.team) return;
    try {
      const result = await convertToAgency.mutateAsync({ teamId: convertDialog.team.id });
      toast.success(`Team ${convertDialog.team.name} converted to brand portfolio!`);
      setConvertDialog({ open: false, team: null });
      navigate(`/admin/agencies/${result.agencyId}`);
    } catch (err) {
      toast.error('Failed to convert team to brand portfolio');
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Error loading teams: {formatApiError(error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Teams"
        subtitle="Manage all platform teams"
        action={<Button onClick={() => setShowCreateModal(true)}>Create Team</Button>}
      />

      {/* Search & Filters */}
      <Card padding="sm">
        <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px]"
          />
          <Select
            value={filters.status || ''}
            onValueChange={(value) => setFilters({ ...filters, status: value as AdminTeamListFilters['status'] || undefined, page: 1 })}
            placeholder="All Statuses"
            options={STATUSES.map(status => ({
              value: status,
              label: status
            }))}
            clearable
            clearLabel="All Statuses"
            size="default"
          />
        </form>
      </Card>

      {/* Teams Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Team</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Package</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Brand Portfolio</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Users</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Expires</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(data?.data ?? []).map((team) => (
                    <tr key={team.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-gray-900">{team.name}</div>
                            <div className="text-sm text-gray-500">{team.email}</div>
                          </div>
                          {team.agencyId && (
                            <Badge variant="primary" className="whitespace-nowrap">
                              Managed Brand
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {team.packageName || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {team.agencyName ? (
                          <a
                            href={`/admin/agencies/${team.agencyId}`}
                            className="text-primary-700 hover:text-primary-900 hover:underline"
                          >
                            {team.agencyName}
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {/* Status-change control — intentionally NOT a Badge, this
                            IS the interactive dropdown, only tinted via statusColors. */}
                        <Select
                          value={team.status}
                          onValueChange={(value) => handleStatusChange(team, value as typeof STATUSES[number])}
                          options={STATUSES.map(status => ({
                            value: status,
                            label: status
                          }))}
                          disabled={changeStatus.isPending}
                          size="sm"
                          className={statusColors[team.status] || 'bg-gray-100'}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {team.userCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {team.expiresAt ? formatDateOnly(team.expiresAt) : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <ActionButton
                            icon={ICONS.CALENDAR}
                            tooltip="Extend Expiry"
                            onClick={() => setExtendDialog({ open: true, team })}
                            variant="success"
                            disabled={extendTrial.isPending}
                          />
                          {!team.agencyId && (
                            <ActionButton
                              icon={ICONS.CROWN}
                              tooltip="Convert to Brand Portfolio"
                              onClick={() => setConvertDialog({ open: true, team })}
                              variant="warning"
                              disabled={convertToAgency.isPending}
                            />
                          )}
                          <ActionButton
                            icon={ICONS.EYE}
                            tooltip="View Details"
                            href={`/admin/teams/${team.id}`}
                          />
                          <ActionButton
                            icon={ICONS.DELETE}
                            tooltip="Delete Team"
                            onClick={() => setDeleteDialog({ open: true, team })}
                            variant="danger"
                            disabled={deleteTeam.isPending}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.data?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState title="No teams found" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {((data.meta.page - 1) * data.meta.limit) + 1} to {Math.min(data.meta.page * data.meta.limit, data.meta.total)} of {data.meta.total}
                </div>
                <Pagination page={data.meta.page} totalPages={data.meta.totalPages} onPageChange={handlePageChange} />
              </div>
            )}
          </>
        )}
      </Card>
      {/* Create Team Modal */}
      {showCreateModal && (
        <CreateTeamModal
          onClose={() => setShowCreateModal(false)}
          createTeam={createTeam}
        />
      )}

      {/* Delete Team Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, team: null })}
        title="Delete Team"
        description={`Are you sure you want to delete "${deleteDialog.team?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteTeam.isPending}
      />

      {/* Extend Expiry Dialog */}
      <PromptDialog
        open={extendDialog.open}
        onOpenChange={(open) => !open && setExtendDialog({ open: false, team: null })}
        title="Extend Expiry"
        description={`Extend expiry for "${extendDialog.team?.name}". Current expiry: ${extendDialog.team?.expiresAt ? formatDateOnly(extendDialog.team.expiresAt) : 'Never'}`}
        label="Days to Extend"
        placeholder="Enter number of days"
        defaultValue="30"
        inputType="number"
        min={1}
        max={365}
        required
        quickSelect={[
          { label: '7 days', value: 7 },
          { label: '14 days', value: 14 },
          { label: '30 days', value: 30 },
          { label: '60 days', value: 60 },
          { label: '90 days', value: 90 },
          { label: '180 days', value: 180 },
          { label: '1 year', value: 365 },
        ]}
        confirmText="Extend"
        cancelText="Cancel"
        variant="success"
        onConfirm={handleExtendConfirm}
        loading={extendTrial.isPending}
      />

      {/* Convert to Agency Dialog */}
      <ConfirmDialog
        open={convertDialog.open}
        onOpenChange={(open) => !open && setConvertDialog({ open: false, team: null })}
        title="Convert to Brand Portfolio"
        description={`Convert "${convertDialog.team?.name}" to a brand portfolio? The team owner will become the brand portfolio owner and this team will be the first client.`}
        confirmText="Convert"
        cancelText="Cancel"
        variant="warning"
        onConfirm={handleConvertConfirm}
        loading={convertToAgency.isPending}
      />
    </div>
  );
};

// ============================================================================
// CREATE TEAM MODAL
// ============================================================================

interface CreateTeamModalProps {
  onClose: () => void;
  createTeam: ReturnType<typeof useCreateTeam>;
}

const CreateTeamModal = ({ onClose, createTeam }: CreateTeamModalProps) => {
  const [formData, setFormData] = useState<CreateTeamRequest>({
    name: '',
    email: '',
    packageId: 0,
    ownerName: '',
    timezone: 'Europe/Berlin',
  });

  const { data: packagesData } = useAdminPackages({ isActive: true });
  const allPackages = (packagesData as { data?: { id: number; name: string; isAgency?: boolean }[] })?.data || [];
  // Filter out agency packages - teams are not agencies
  const packages = allPackages.filter(pkg => !pkg.isAgency);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Team name is required');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!formData.packageId) {
      toast.error('Please select a package');
      return;
    }

    try {
      const result = await createTeam.mutateAsync(formData) as CreateTeamResponse;
      toast.success(`Team "${result.team.name}" created! Password setup email sent to ${result.user.email}`);
      onClose();
    } catch (err) {
      toast.error('Failed to create team');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Create New Team"
      size="sm"
      footer={
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={createTeam.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="create-team-form" loading={createTeam.isPending}>
            Create Team
          </Button>
        </ModalFooter>
      }
    >
      <form id="create-team-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Name *
          </label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Acme Corp"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="admin@example.com"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Password setup email will be sent to this address
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Owner Name (optional)
          </label>
          <Input
            type="text"
            value={formData.ownerName || ''}
            onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
            placeholder="John Doe"
          />
          <p className="text-xs text-gray-500 mt-1">
            Defaults to team name if not provided
          </p>
        </div>

        <div>
          <Select
            label="Package *"
            value={formData.packageId.toString()}
            onValueChange={(value) => setFormData({ ...formData, packageId: parseInt(value, 10) })}
            placeholder="Select a package..."
            options={[
              { value: '0', label: 'Select a package...' },
              ...packages.map((pkg) => ({
                value: pkg.id.toString(),
                label: pkg.name
              }))
            ]}
          />
        </div>

        <div>
          <DatePickerSingle
            label="Expiry Date (optional)"
            value={formData.expiresAt || ''}
            onChange={(iso) => setFormData({ ...formData, expiresAt: iso || null })}
            placeholder="dd.mm.yyyy"
            minDate={new Date()}
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty for no expiry
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default AdminTeams;
